import { motion } from 'framer-motion';
import { PropsWithChildren, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import css from './ImpersonationToolbar.module.scss';

interface IImpersonationToolbarActionsProps extends PropsWithChildren {}

const ImpersonationToolbarActions = ({
  children,
}: IImpersonationToolbarActionsProps) => {
  return <div className={css.actions}>{children}</div>;
};

interface IImpersonationToolbarContentProps extends PropsWithChildren {}

const ImpersonationToolbarContent = ({
  children,
}: IImpersonationToolbarContentProps) => {
  return <div className={css.content}>{children}</div>;
};

interface IImpersonationToolbarProps extends PropsWithChildren {
  isVisible: boolean;
}

const motionProps = {
  initial: { opacity: 0, translateY: 10 },
  animate: { opacity: 1, translateY: 0 },
  transition: { duration: 0.3 },
};

const ImpersonationToolbar = ({
  children,
  isVisible,
}: IImpersonationToolbarProps) => {
  const toolbarRef = useRef<HTMLDivElement>();

  // Workaround for re-positioning the toolbar when the window is resized.
  // See:
  // - https://github.com/react-grid-layout/react-draggable/issues/363#issuecomment-474406174
  // - https://github.com/react-grid-layout/react-draggable/issues/622#issuecomment-1356254892
  useEffect(() => {
    const triggerMouseEvent = (
      element: Document | HTMLElement,
      eventType: string
    ) => {
      const mouseEvent = new Event(eventType, {
        bubbles: true,
        cancelable: true,
      });
      element.dispatchEvent(mouseEvent);
    };

    const handleResize = () => {
      if (toolbarRef.current) {
        triggerMouseEvent(toolbarRef.current, 'mouseover');
        triggerMouseEvent(toolbarRef.current, 'mousedown');
        setTimeout(() => {
          triggerMouseEvent(document, 'mousemove');
          triggerMouseEvent(toolbarRef.current, 'mouseup');
          triggerMouseEvent(toolbarRef.current, 'click');
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    isVisible && (
      <Draggable bounds="body" nodeRef={toolbarRef}>
        <div className={css.wrapper} ref={toolbarRef}>
          <motion.div className={css.toolbar} {...motionProps}>
            {children}
          </motion.div>
        </div>
      </Draggable>
    )
  );
};

ImpersonationToolbar.Actions = ImpersonationToolbarActions;
ImpersonationToolbar.Content = ImpersonationToolbarContent;

export { ImpersonationToolbar };
